import { Mark, Node } from "@tiptap/core";
import type { Editor } from "@tiptap/core";
import { TextSelection } from "@tiptap/pm/state";
import { ReactNodeViewRenderer } from "@tiptap/react";

import { fieldsSuggestionKey } from "./fields-suggestion";
import { FieldView } from "./field-view";
import { ImageView } from "./image-view";
import { PatternView } from "./pattern-view";
import { SignatureRowView, SignatureSlotView, SignatureView } from "./signature-view";
import { StepTitle, StepView } from "./step-view";
import { DocumentTable } from "./table";
import { TableView } from "./table-view";

function suggestionOpen(editor: Editor) {
	return fieldsSuggestionKey.getState(editor.state)?.active === true;
}

function alignAttr(dom: HTMLElement) {
	return dom.dataset.align === "center" ? "center" : null;
}

function alignHtmlAttrs(align: unknown) {
	if (align === "center") {
		return { class: "center", "data-align": "center" };
	}

	return {};
}

export const DocNode = Node.create({
	name: "doc",
	topNode: true,
	content: "block+",
});

export const TextNode = Node.create({
	name: "text",
	group: "inline",
});

export const BoldMark = Mark.create({
	name: "bold",

	parseHTML() {
		return [{ tag: "strong" }, { tag: "b" }];
	},

	renderHTML() {
		return ["strong", 0];
	},

	addKeyboardShortcuts() {
		return {
			"Mod-b": () => this.editor.commands.toggleMark("bold"),
		};
	},
});

export const UnderlineMark = Mark.create({
	name: "underline",

	parseHTML() {
		return [{ tag: "u" }];
	},

	renderHTML() {
		return ["u", 0];
	},

	addKeyboardShortcuts() {
		return {
			"Mod-u": () => this.editor.commands.toggleMark("underline"),
		};
	},
});

export const ItalicMark = Mark.create({
	name: "italic",

	parseHTML() {
		return [{ tag: "em" }, { tag: "i" }];
	},

	renderHTML() {
		return ["em", 0];
	},

	addKeyboardShortcuts() {
		return {
			"Mod-i": () => this.editor.commands.toggleMark("italic"),
		};
	},
});

export const HeadingNode = Node.create({
	name: "heading",
	group: "block",
	content: "inline*",
	defining: true,

	addAttributes() {
		return { level: { default: 2 }, align: { default: null } };
	},

	parseHTML() {
		return [
			{ tag: "h2", getAttrs: (dom) => ({ level: 2, align: alignAttr(dom) }) },
			{ tag: "h3", getAttrs: (dom) => ({ level: 3, align: alignAttr(dom) }) },
			{ tag: "h4", getAttrs: (dom) => ({ level: 4, align: alignAttr(dom) }) },
		];
	},

	renderHTML({ node }) {
		return [`h${node.attrs.level}`, alignHtmlAttrs(node.attrs.align), 0];
	},
});

export const ParagraphNode = Node.create({
	name: "paragraph",
	group: "block",
	content: "inline*",

	addAttributes() {
		return { emphasis: { default: null }, align: { default: null } };
	},

	parseHTML() {
		return [
			{ tag: "p", getAttrs: (dom) => ({ emphasis: dom.dataset.emphasis, align: alignAttr(dom) }) },
		];
	},

	renderHTML({ node }) {
		const classes: string[] = [];
		const attrs: Record<string, string> = {};

		if (node.attrs.emphasis !== null) {
			classes.push(node.attrs.emphasis);
			attrs["data-emphasis"] = node.attrs.emphasis;
		}

		if (node.attrs.align === "center") {
			classes.push("center");
			attrs["data-align"] = "center";
		}

		if (classes.length > 0) {
			attrs.class = classes.join(" ");
		}

		return ["p", attrs, 0];
	},

	addKeyboardShortcuts() {
		return {
			Enter: () => {
				const { $from, empty } = this.editor.state.selection;

				if (!empty || $from.parent.type.name !== "paragraph") {
					return false;
				}

				if ($from.parent.content.size > 0) {
					if ($from.parent.attrs.emphasis === null) {
						return false;
					}

					if ($from.parentOffset !== $from.parent.content.size) {
						return false;
					}

					if (suggestionOpen(this.editor)) {
						return false;
					}

					const tr = this.editor.state.tr;

					tr.split($from.pos, 1, [{ type: $from.parent.type, attrs: { emphasis: null } }]);
					this.editor.view.dispatch(tr.scrollIntoView());

					return true;
				}

				if ($from.parent.attrs.emphasis !== null) {
					return this.editor.commands.setNode("paragraph", { emphasis: null });
				}

				if ($from.depth !== 2 || $from.node(1).type.name !== "variantCard") {
					return false;
				}

				const card = $from.node(1);

				if ($from.index(1) !== card.childCount - 1) {
					return false;
				}

				const tr = this.editor.state.tr;

				if (card.childCount === 1) {
					tr.replaceWith($from.before(1), $from.after(1), $from.parent);
					tr.setSelection(TextSelection.create(tr.doc, $from.before(1) + 1));
				} else {
					tr.delete($from.before(), $from.after());

					const pos = tr.mapping.map($from.after(1));

					tr.insert(pos, $from.parent.type.create());
					tr.setSelection(TextSelection.create(tr.doc, pos + 1));
				}

				this.editor.view.dispatch(tr.scrollIntoView());

				return true;
			},
		};
	},
});

export const ListItemNode = Node.create({
	name: "listItem",
	group: "block",
	content: "inline*",

	parseHTML() {
		return [{ tag: "li" }];
	},

	renderHTML() {
		return ["li", 0];
	},

	addKeyboardShortcuts() {
		return {
			Enter: () => {
				const { $from, empty } = this.editor.state.selection;

				if ($from.parent.type.name !== "listItem") {
					return false;
				}

				if (empty && $from.parent.content.size === 0) {
					return this.editor.commands.setNode("paragraph");
				}

				if (suggestionOpen(this.editor)) {
					return false;
				}

				const tr = this.editor.state.tr;

				tr.deleteSelection();
				tr.split(tr.selection.from, 1, [{ type: this.editor.state.schema.nodes.listItem }]);
				this.editor.view.dispatch(tr.scrollIntoView());

				return true;
			},
		};
	},
});

export const StepNode = Node.create({
	name: "step",
	group: "block",
	content: "inline*",
	defining: true,
	isolating: true,

	addAttributes() {
		return { title: { default: "" } };
	},

	parseHTML() {
		return [
			{
				tag: "div[data-step]",
				getAttrs: (dom) => ({ title: dom.dataset.step }),
				contentElement: "p:not(.step-title)",
			},
		];
	},

	renderHTML({ node }) {
		return [
			"div",
			{ class: "step", "data-step": node.attrs.title },
			["p", { class: "step-title" }, node.attrs.title],
			["p", 0],
		];
	},

	addNodeView() {
		return ReactNodeViewRenderer(StepView);
	},

	addKeyboardShortcuts() {
		return {
			Enter: () => {
				const { $from, empty } = this.editor.state.selection;

				if ($from.parent.type.name !== "step") {
					return false;
				}

				if (empty && $from.parent.content.size === 0 && $from.parent.attrs.title === "") {
					return this.editor.commands.setNode("paragraph");
				}

				if (suggestionOpen(this.editor)) {
					return false;
				}

				const tr = this.editor.state.tr;

				tr.deleteSelection();
				tr.split(tr.selection.from, 1, [
					{ type: this.editor.state.schema.nodes.step, attrs: { title: "" } },
				]);
				this.editor.view.dispatch(tr.scrollIntoView());

				return true;
			},
			Backspace: () => {
				const { $from, empty } = this.editor.state.selection;

				if (!empty || $from.parent.type.name !== "step" || $from.parentOffset !== 0) {
					return false;
				}

				if ($from.parent.attrs.title !== "") {
					return StepTitle.focus(this.editor, $from.before());
				}

				return this.editor.commands.setNode("paragraph");
			},
			ArrowUp: () => {
				const { $from, empty } = this.editor.state.selection;

				if (!empty || $from.parent.type.name !== "step") {
					return false;
				}

				if (!this.editor.view.endOfTextblock("up")) {
					return false;
				}

				return StepTitle.focus(this.editor, $from.before());
			},
		};
	},
});

export const TableCellNode = Node.create({
	name: "tableCell",
	content: "inline*",
	isolating: true,

	parseHTML() {
		return [{ tag: "td" }, { tag: "th" }];
	},

	renderHTML() {
		return ["td", 0];
	},
});

export const TableRowNode = Node.create({
	name: "tableRow",
	content: "tableCell+",

	parseHTML() {
		return [{ tag: "tr" }];
	},

	renderHTML() {
		return ["tr", 0];
	},
});

export const TableNode = Node.create({
	name: "table",
	group: "block",
	content: "tableRow+",
	isolating: true,

	parseHTML() {
		return [{ tag: "table" }];
	},

	renderHTML() {
		return ["table", ["tbody", 0]];
	},

	addNodeView() {
		return ReactNodeViewRenderer(TableView, { contentDOMElementTag: "tbody" });
	},

	addKeyboardShortcuts() {
		return {
			Tab: () => DocumentTable.next(this.editor),
			"Shift-Tab": () => DocumentTable.previous(this.editor),
			Enter: () => {
				if (suggestionOpen(this.editor)) {
					return false;
				}

				return DocumentTable.rowBelow(this.editor);
			},
			Backspace: () => DocumentTable.backspace(this.editor),
		};
	},
});

export const VariantCardNode = Node.create({
	name: "variantCard",
	group: "block",
	content: "(paragraph | heading | listItem | step | table)+",
	defining: true,
	isolating: true,

	addAttributes() {
		return { variant: { default: "info" } };
	},

	parseHTML() {
		return [
			{
				tag: "div[data-variant]",
				getAttrs: (dom) => ({ variant: dom.dataset.variant }),
			},
		];
	},

	renderHTML({ node }) {
		return [
			"div",
			{ class: `card card-${node.attrs.variant}`, "data-variant": node.attrs.variant },
			0,
		];
	},
});

export const PageBreakNode = Node.create({
	name: "pageBreak",
	group: "block",
	atom: true,
	selectable: true,

	parseHTML() {
		return [{ tag: "div[data-page-break]" }];
	},

	renderHTML() {
		return ["div", { class: "page-break", "data-page-break": "" }];
	},
});

export const ImageNode = Node.create({
	name: "image",
	group: "block",
	atom: true,
	selectable: true,
	draggable: true,

	addAttributes() {
		return {
			file: { default: "" },
			width: { default: 0 },
			height: { default: 0 },
			align: { default: null },
		};
	},

	parseHTML() {
		return [
			{
				tag: "figure[data-image-file]",
				getAttrs: (dom) => ({
					file: dom.dataset.imageFile,
					width: Number(dom.dataset.imageWidth),
					height: Number(dom.dataset.imageHeight),
					align:
						dom.dataset.align === "center" || dom.dataset.align === "right"
							? dom.dataset.align
							: null,
				}),
			},
		];
	},

	renderHTML({ node }) {
		const { file, width, height, align } = node.attrs;
		const attrs: Record<string, string> = {
			class: align === null ? "doc-image" : `doc-image ${align}`,
			"data-image-file": file,
			"data-image-width": String(width),
			"data-image-height": String(height),
		};

		if (align !== null) {
			attrs["data-align"] = align;
		}

		return [
			"figure",
			attrs,
			[
				"img",
				{ src: `/terms/image/${file}`, style: `width:${width}px;height:${height}px`, alt: "" },
			],
		];
	},

	addNodeView() {
		return ReactNodeViewRenderer(ImageView);
	},
});

export const PatternNode = Node.create({
	name: "pattern",
	group: "block",
	content: "(paragraph | heading | listItem | step | table | variantCard | signature | image)+",
	defining: true,
	isolating: true,

	addAttributes() {
		return { patternId: { default: 0 } };
	},

	parseHTML() {
		return [
			{
				tag: "div[data-pattern-id]",
				getAttrs: (dom) => ({ patternId: Number(dom.dataset.patternId) }),
			},
		];
	},

	renderHTML({ node }) {
		return [
			"div",
			{ class: "document-pattern", "data-pattern-id": String(node.attrs.patternId) },
			0,
		];
	},

	addNodeView() {
		return ReactNodeViewRenderer(PatternView);
	},
});

export const FieldNode = Node.create({
	name: "field",
	group: "inline",
	inline: true,
	atom: true,
	selectable: true,

	addAttributes() {
		return { fieldId: { default: 0 } };
	},

	parseHTML() {
		return [
			{
				tag: "span[data-field-id]",
				getAttrs: (dom) => ({ fieldId: Number(dom.dataset.fieldId) }),
			},
		];
	},

	renderHTML({ node }) {
		return ["span", { "data-field-id": String(node.attrs.fieldId) }];
	},

	addNodeView() {
		return ReactNodeViewRenderer(FieldView);
	},
});

export const SignatureNode = Node.create({
	name: "signature",
	group: "block",
	content: "signatureRow+",
	defining: true,
	isolating: true,

	parseHTML() {
		return [{ tag: "div.signature" }];
	},

	renderHTML() {
		return ["div", { class: "signature" }, 0];
	},

	addNodeView() {
		return ReactNodeViewRenderer(SignatureView);
	},
});

export const SignatureRowNode = Node.create({
	name: "signatureRow",
	content: "signatureSlot{1,2}",
	isolating: true,

	parseHTML() {
		return [{ tag: "div.signature-row" }];
	},

	renderHTML() {
		return ["div", { class: "signature-row" }, 0];
	},

	addNodeView() {
		return ReactNodeViewRenderer(SignatureRowView);
	},
});

export const SignatureSlotNode = Node.create({
	name: "signatureSlot",
	content: "inline*",
	isolating: true,

	parseHTML() {
		return [{ tag: "div.signature-slot" }];
	},

	renderHTML() {
		return [
			"div",
			{ class: "signature-slot" },
			["div", { class: "signature-line" }],
			["p", { class: "signature-label" }, 0],
		];
	},

	addNodeView() {
		return ReactNodeViewRenderer(SignatureSlotView);
	},
});
